/* ═══════════════════════════════════════════════════════════
   PROOF — page-specific content styles only.
   Deck mechanics (scroll engine, sections, progress dots, tuner,
   back-to-top) live in vite/components/deck/deck.module.css.
   Copy, screenshots, wordmark and accent come from proofdelivery.app.
   ═══════════════════════════════════════════════════════════ */

.page {
  --proof-accent: #14b8a6;
  --proof-accent-soft: rgba(20, 184, 166, 0.16);
  --proof-accent-contrast: #04201d;
  display: contents;
}

/* ──────────── COMMON TYPE ──────────── */
.eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--proof-accent);
  text-align: center;
}

.bigTitle {
  font-family: var(--font-heading);
  font-size: clamp(2rem, 5vw, 4rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  margin: 0;
  background: linear-gradient(180deg, #ffffff 0%, #c8c8d0 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.midTitle {
  font-family: var(--font-heading);
  font-size: clamp(1.5rem, 3.4vw, 2.4rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-align: center;
  margin: 0;
  color: var(--color-text-primary);
}

.lede {
  max-width: 720px;
  text-align: center;
  font-size: clamp(0.92rem, 1vw, 1.04rem);
  color: var(--color-text-secondary);
  line-height: 1.65;
}

/* ──────────── HERO ──────────── */
.heroLayout {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  align-items: center;
  gap: clamp(1.5rem, 3vw, 3rem);
  width: 100%;
}

.heroCopy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.1rem;
}

.heroCopy .lede {
  text-align: left;
  margin: 0;
}

.wordmark {
  display: block;
  height: clamp(28px, 3vw, 44px);
  width: auto;
}

.storeRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.4rem;
}

.storeButton {
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.55rem 1.3rem;
  border-radius: 9999px;
  background: var(--proof-accent);
  color: var(--proof-accent-contrast);
  text-decoration: none;
  line-height: 1.15;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.storeButton:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(20, 184, 166, 0.28);
}

.storeSub {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.storeMain {
  font-size: 1rem;
  font-weight: 700;
}

.heroPhoneSlot {
  display: flex;
  justify-content: center;
}

.heroPhoneSlot > div {
  transform: scale(0.85);
  transform-origin: center;
}

/* ──────────── CARDS (why / more than galleries / who it's for) ──────────── */
.cardGrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.9rem;
  width: 100%;
  max-width: 1100px;
  margin-top: 0.6rem;
}

.cardGridFour {
  grid-template-columns: repeat(4, 1fr);
}

.card {
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-default);
  border-radius: 14px;
  padding: 1.2rem 1.3rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  height: 100%;
}

.cardLabel {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--proof-accent);
}

.cardTitle {
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.cardBody {
  color: var(--color-text-secondary);
  font-size: 0.86rem;
  line-height: 1.55;
}

a.card {
  text-decoration: none;
  transition: border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease;
}

a.card:hover {
  border-color: var(--proof-accent);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(20, 184, 166, 0.08);
}

.cardArrow {
  margin-top: auto;
  font-size: 0.84rem;
  color: var(--color-text-secondary);
}

/* ──────────── SEE IT WORKING (gallery) ──────────── */
.galleryGrid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  width: 100%;
  max-width: 1080px;
  margin: 1.2rem auto 0;
  align-items: start;
}

.galleryItem {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.galleryShot {
  display: block;
  width: 100%;
  border-radius: 20px;
  border: 1px solid var(--color-border-default);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.4);
}

.galleryCaption {
  font-size: 0.78rem;
  line-height: 1.3;
  text-align: center;
  color: var(--color-text-secondary);
}

/* ──────────── CLOSING LINKS ──────────── */
.closingLinks {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.4rem;
  margin-top: 0.6rem;
  font-size: 0.86rem;
}

.closingLinks a {
  color: var(--color-text-secondary);
  text-decoration: none;
}

.closingLinks a:hover {
  color: var(--proof-accent);
}

/* ──────────── RESPONSIVE ──────────── */
@media (max-width: 880px) {
  .heroLayout { grid-template-columns: 1fr; gap: 1rem; }
  .heroCopy { align-items: center; }
  .heroCopy .lede { text-align: center; }
  .heroCopy .bigTitle { text-align: center; }
  .wordmark { margin: 0 auto; }
  .storeRow { justify-content: center; }

  .cardGrid,
  .cardGridFour { grid-template-columns: 1fr; }

  .galleryGrid { grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
}

@media (max-width: 440px) {
  .galleryGrid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-height: 720px) {
  .heroPhoneSlot { display: none; }
  .heroLayout { grid-template-columns: 1fr; }
  .heroCopy { align-items: center; text-align: center; }
  .heroCopy .lede, .heroCopy .bigTitle { text-align: center; }
  .wordmark { margin: 0 auto; }
  .storeRow { justify-content: center; }
}
